import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag, Loader2, ExternalLink, ShieldAlert, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange";
const card = "border border-white/10 bg-black/40 p-5 flex flex-col gap-4";

const FILTERS = [["", "All"], ["open", "Open"], ["under_review", "Under review"], ["resolved", "Resolved"], ["dismissed", "Dismissed"]];
const STATUS_CLS = { open: "text-nc-orange border-nc-orange/40", under_review: "text-amber-400 border-amber-500/40", resolved: "text-nc-green border-nc-green/40", dismissed: "text-zinc-500 border-white/15" };
const CAT_CLS = { cheating: "text-red-400 border-red-500/40", rdm: "text-nc-orange border-nc-orange/40", vdm: "text-nc-orange border-nc-orange/40", exploit: "text-red-300 border-red-400/40" };

/* Reports — the server's player-report queue. Owners triage; Specpixel studio
   (admin) sees every server and can escalate cheating reports to a permanent
   global ban in one click. */
export function ReportsPanel({ sid, isAdmin }) {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/nc/reports", { params: { server_id: sid, status: filter } }).then((r) => setReports(r.data.reports)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [sid, filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      const r = await api.post(`/nc/reports/${id}/status`, { status, note });
      toast.success(r.data.message);
      setNote("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(""); }
  };

  const escalate = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/nc/reports/${id}/escalate`);
      toast.success(r.data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div className={card}>
      <div className="flex items-center gap-3 flex-wrap">
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><Flag className="w-3.5 h-3.5" /> Player reports</p>
        {isAdmin && <span className="font-mono text-[8px] uppercase tracking-[0.15em] text-red-400 border border-red-500/40 px-2 py-1 flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Studio access — all servers</span>}
        <div className="ml-auto flex gap-1.5 flex-wrap">
          {FILTERS.map(([v, l]) => (
            <button key={v} data-testid={`reports-filter-${v || "all"}`} onClick={() => setFilter(v)}
              className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2.5 py-1.5 border transition-colors ${filter === v ? "border-nc-orange text-nc-orange bg-nc-orange/5" : "border-white/10 text-zinc-500 hover:border-white/30"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {reports.length === 0 && <p className="font-mono text-[10px] text-zinc-500">No reports in this view — clean streets.</p>}

      {reports.map((r) => (
        <div key={r.id} data-testid={`report-row-${r.id.slice(0, 8)}`} className="border border-white/10 bg-[#070707]">
          <button data-testid={`report-open-${r.id.slice(0, 8)}`} onClick={() => { setOpen(open === r.id ? "" : r.id); setNote(""); }} className="w-full px-4 py-3 flex items-center gap-3 flex-wrap text-left">
            <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${CAT_CLS[r.category] || "text-zinc-400 border-white/15"}`}>{r.category || "other"}</span>
            <span className="font-sans font-semibold text-sm text-zinc-100">{r.target_name}{r.target_playercode ? <span className="text-zinc-500 font-mono text-[10px]"> #{r.target_playercode}</span> : null}</span>
            <span className="font-mono text-[9px] text-zinc-500">by {r.reporter}</span>
            <span className="font-mono text-[9px] text-zinc-600">{String(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
            <span className={`ml-auto font-mono text-[8px] uppercase border px-1.5 py-0.5 ${STATUS_CLS[r.status] || STATUS_CLS.open}`}>{(r.status || "open").replace("_", " ")}</span>
            {open === r.id ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
          </button>
          {open === r.id && (
            <div className="border-t border-white/5 px-4 py-3 flex flex-col gap-3">
              <p className="font-mono text-[10px] text-zinc-300 leading-relaxed whitespace-pre-wrap">{r.description}</p>
              {(r.evidence || []).length > 0 && (
                <div className="flex flex-col gap-1">
                  <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500">Evidence</span>
                  {r.evidence.map((u) => (
                    <a key={u} href={u} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-nc-blue hover:underline flex items-center gap-1.5 break-all"><ExternalLink className="w-3 h-3 shrink-0" />{u}</a>
                  ))}
                </div>
              )}
              {r.handled_by && <p className="font-mono text-[9px] text-zinc-500">Handled by {r.handled_by}{r.note ? ` — ${r.note}` : ""}</p>}
              <div><span className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5">Resolution note (optional)</span><input data-testid={`report-note-${r.id.slice(0, 8)}`} className={inputCls} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Warned in-game; watching for repeats" /></div>
              <div className="flex gap-2 flex-wrap">
                {[["under_review", "Under review"], ["resolved", "Resolve"], ["dismissed", "Dismiss"]].map(([st, l]) => (
                  <button key={st} data-testid={`report-${st}-${r.id.slice(0, 8)}`} disabled={busy === r.id || r.status === st} onClick={() => setStatus(r.id, st)}
                    className="font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-2 border border-white/15 text-zinc-300 hover:border-nc-orange hover:text-nc-orange transition-colors disabled:opacity-40 flex items-center gap-1.5">
                    {busy === r.id && <Loader2 className="w-3 h-3 animate-spin" />}{l}
                  </button>
                ))}
                {isAdmin && r.category === "cheating" && r.status !== "resolved" && (
                  <button data-testid={`report-escalate-${r.id.slice(0, 8)}`} disabled={busy === r.id} onClick={() => escalate(r.id)}
                    className="font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40 flex items-center gap-1.5">
                    <ShieldAlert className="w-3 h-3" /> Escalate to global ban
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
